import { createContext, ReadOnlyRef, Ref, useEffect, useRef, useState } from '@lukekaalim/act';
import * as three from 'three';
import { Observable, Subject } from 'rxjs';
import { createJanitor, KeyboardInput, subscribeEvent, useKeyboardInput, useLogger } from '@kosmod/kit';
import { DrivableCameraInstance, Graphics3DSetup, inputBeat, K3D, renderBeat, Snap, uiBeat, useGraphics3DSetup } from '@kosmod/graphics-3d';
import { TheatreSelectionController, useTheatreSelectionController } from './useTheatreSelection';
import { TheatreController, TheatreData } from './useTheatreController';
import { TheatreCoreResources } from './useTheatreCoreResources';
import { PhysicsDiceID, SurfaceID, theatreAPI, TheatrePosition } from '../defs';
import { useTheatreCursor } from './useCursor';
import { ToolController, useActiveToolHandlers, useToolController } from './useToolController';
import { ConciergeState } from '@kosmod/concierge';
import { HttpApi, NetworkClient } from '@kosmod/pancake';
import { assetsAPI, GameAssetClient } from '@kosmod/assets';
import { DiceTemplate } from '@kosmod/andy';
import { TheatreSceneResources } from './useTheatreSceneResources';
import { ReadonlyChunkMap } from '@kosmod/stew';
import { TerrainSystemData, WalkableNode } from '../components/TerrainSystems/terrain';
import { SurfaceData, useSurfaceData } from './useSurfaceData';
import { usePhysics } from './usePhysics';
import { useHostSync } from './useHostSync';
import { TheatreContentManager, useTheatreContentManager } from './useTheatreContentManager';

export type TheatreCutout = {
  scene: ReadOnlyRef<null | three.Scene>,
  camera: ReadOnlyRef<null | three.Camera>,
  rect: DOMRect
}

export type TheatreState = {
  concierge: ConciergeState,

  content: TheatreContentManager,

  viewMode: 'scene' | 'content-browser',
  changeViewMode(newViewMode: 'scene' | 'content-browser'): void,

  /**
   * Where the cursor is in Theatre-Space, if present at all.
   */
  cursorPosition: Ref<null | TheatrePosition>,
  cursor2DPosition: Ref<null | three.Vector2>,
  /**
   * Where the camera is focused on in World-Space
   */
  camera: Ref<null | DrivableCameraInstance>,

  containerRef: Ref<null | HTMLElement>,

  cutouts: Ref<TheatreCutout[]>,

  /**
   * 
   */
  events: {
    pointerClick: Observable<MouseEvent>,
    contextClick: Observable<MouseEvent>, 
  }

  clients: {
    net: NetworkClient,
    theatre: HttpApi.Handler<"client", typeof theatreAPI>,
    assets: GameAssetClient,
  },

  surfaceData: Map<SurfaceID, SurfaceData>,


  keyboard: KeyboardInput,

  tools: ToolController,
  selection: TheatreSelectionController,
  graphics: Graphics3DSetup,

  data: TheatreData,
  controller: TheatreController,
  res: TheatreCoreResources,
  sceneRes: TheatreSceneResources,

  physics: null | Snap.Simulation,
  localDiceTransforms: Map<PhysicsDiceID, K3D.Transform>
}

export const TheatreStateContext = createContext<TheatreState>(null as any);

export const useTheatreState = (
  concierge: ConciergeState,
  ref: ReadOnlyRef<HTMLElement | null>,
  controller: TheatreController,
  data: TheatreData,
  res: TheatreCoreResources,
  sceneRes: TheatreSceneResources,
  clients: {
    net: NetworkClient,
    theatre: HttpApi.Handler<"client", typeof theatreAPI>,
    assets: GameAssetClient,
  },
): TheatreState => {
  const inputTick = inputBeat.useTick();
  const renderTick = renderBeat.useTick();
  const uiTick = uiBeat.useTick();
  
  const logger = useLogger('TheatreState');
  const [viewMode, setViewMode] = useState<TheatreState['viewMode']>('scene');

  const containerRef = useRef<HTMLElement | null>(null);

  const graphics = useGraphics3DSetup({
    onFrame(now) {
      inputTick(now);
      renderTick(now);
      uiTick(now);

      clearKeyboardFrameInput();
    },
    // afterRender() {
    //   const renderer = graphics.renderer.get()!;
    //   const cutouts = cutoutsRef.get();
    //   renderer.autoClear = false
    //   renderer.setScissorTest(true)

    //   const canvasRect = graphics.canvas.get()!.getBoundingClientRect();

    //   for (const cutout of cutouts) {
    //     const y = canvasRect.height - cutout.rect.y + canvasRect.y - cutout.rect.height;

    //     renderer.setScissor(cutout.rect.x, y, cutout.rect.width, cutout.rect.height);
    //     renderer.setViewport(cutout.rect.x, y, cutout.rect.width, cutout.rect.height);
    //     renderer.render(cutout.scene.get()!, cutout.camera.get()!);
    //   }
    //   renderer.setViewport(0, 0, canvasRect.width, canvasRect.height)
    //   renderer.setScissorTest(false)
    //   renderer.autoClear = true
    // },
  });

  const selection = useTheatreSelectionController(ref);
  const [keyboard, clearKeyboardFrameInput] = useKeyboardInput(ref, []);

  const [cursorPosition, cursor2DPosition] = useTheatreCursor(
    graphics.camera, graphics.canvas, graphics.canvas, ref, controller, data.defaultSurfaceId, data.surfaces, viewMode === 'scene'
  );
  const camera = useRef(null);
  const surfaceData = useSurfaceData(data.surfaces, data.canvases, data.minis, data.chunks, data.canvasCoordinator);

  const [events] = useState(() => ({
    pointerClick: new Subject<MouseEvent>(),
    contextClick: new Subject<MouseEvent>(),
  }))

  useEffect(() => {
    const input = graphics.canvas.get();
    if (!input || viewMode !== 'scene')
      return;

    const j = createJanitor();

    j.add(subscribeEvent(input, 'click', e => !e.defaultPrevented && events.pointerClick.next(e)))
    j.add(subscribeEvent(input, 'contextmenu', e => !e.defaultPrevented && events.contextClick.next(e)))

    return j.cleanup;
  }, [ref, viewMode]);

  const tools = useToolController(selection, controller, data);

  const cutoutsRef = useRef<TheatreCutout[]>([])


  const [physics, localDiceTransforms] = usePhysics();

  useHostSync(cursorPosition, localDiceTransforms, controller)


  const content = useTheatreContentManager(
    data.game,
    data,
    controller,
    clients.assets,
    clients.theatre
  );

  const state: TheatreState = {
    concierge,
    containerRef,
    cursorPosition,
    cursor2DPosition,
    camera,
    events,
    tools,
    keyboard,
    selection,
    graphics,

    cutouts: cutoutsRef,

    viewMode,
    changeViewMode: setViewMode,

    surfaceData,

    data,
    controller,
    res,
    sceneRes,
    clients,
    
    localDiceTransforms,
    physics,
    content,
  }

  useActiveToolHandlers(state);

  // logger.log({ state })

  return state;
}
